/* eslint-disable no-undef */
import React, { useState } from 'react';
import 'src/styles/fonts/MCGlobalFonts.css';
import Spinner from 'src/common/refactoredComponents/spinner/Spinner';
import { Login, ErrorComponent } from 'src/constants/routes';
import SSO from 'src/sso/component';
import {
    getUrlparams,
    checkImpersonationActive,
    getRoutePathWithParams,
    checkProxyActive,
    isCorrectURL,
    logoutWithCleanUp,
    convertDateToISOString,
    displayDeniedOverride,
    logErrorToConsole,
} from 'src/utils/util.js';
import {
    BrowserRouter as Router,
    Route,
    Switch,
    Redirect,
} from 'react-router-dom';
import {
    AuthenticatedTemplate,
    UnauthenticatedTemplate,
    useMsal,
} from '@azure/msal-react';
import AccessToken from './sso/component/AccessToken';
import PageLayout from './common/components/PageLayout';
import styled from 'styled-components';
import media from 'src/styles/Media.styled';
import {
    getAuthToken,
    getBannerDetails,
    getConfigurations,
    getHybrisUserDetails,
} from 'src/common/redux/actions';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthenticatedRoute from 'src/common/components/authenticatedRoute';
import useNonInitialRenderHook from 'src/common/hooks/useNonInitialRenderHook';
import SessionTimeout from './utils/sessionTimeOut';
import { routes } from 'src/constants/routes';
import { validateUserRole, detectDeviceInfo } from 'src/utils/util';
import {
    removeImperonatedUserDetail,
    clearStorageBrowserClose,
    removeProxyUserDetail,
} from 'src/utils/util';
import { setImpersonateCheckAction } from 'src/pages/impersonation/redux/actions/index.js';
import Admin from 'src/pages/admin';
import Home from 'src/pages/home';
import Orders from 'src/pages/orders';
import ReceiveSets from 'src/pages/receiveSets';
import {
    CC_USER_ROLE,
    ITA_USER_ROLE,
    CSR_USER_ROLE,
    EQUITY_USER_PAGE_VISIBILITY_RULE,
    FIM_USER_ROLE,
    setPageVisibilityRule,
    FIA_USER_ROLE,
    MENU,
} from 'src/constants/appConstants';
import { useHistory } from 'react-router-dom';
import { getUser } from './pages/equity/components/common/utils/eventHandlerUtil';
import { logout as logoutAPI } from 'src/common/redux/actions';
import { getPageVisibilityRule } from './pages/home/redux/actions';
import {
    actionApplyFilterType,
    getDeniedOrder,
    getRefinedOrderListData,
    refineFilterAndOrderList,
    setDeniedOverrideClicked,
    setOrderDateAction,
    setSearchParams,
} from './pages/orders/redux/actions';
import { addDaysToDate } from './utils/dateUtils';
import { getProxyList } from 'src/pages/more/pages/proxies/redux/action';
import { useEffectWithErrorLogging } from 'src/utils/useEffectWithErrorLogging';
import AppErrorBoundary from 'src/errorHandler/AppErrorBoundary.jsx';

const App = () => {
    const dispatch = useDispatch();
    const PAGE_LIMIT = 50;

    const { instance } = useMsal();
    const history = useHistory();
    const countryISOCode = useSelector(
        (state) => state.loginReducer.hybrisUserDetails.countryISOCode
    );
    const employeeRole = useSelector(
        (state) => state.loginReducer.hybrisUserDetails.employeeRole
    );
    // Set the base site id for the selected language
    const { t } = useTranslation();
    // const { i18n } = useTranslation();
    // localStorage.setItem('baseSiteId', t('base_site_id'));
    const [, setFontsLoaded] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);
    const { filter } = useSelector((state) => state.appliedOrdersFilterReducer);
    const { endDate } = filter;
    const { calendar: calendarMainMenuVisibility, orders: ordersMainMenuVisibility, inventory: inventoryMainMenuVisibility, admin: adminMainMenuVisibility } =
        useSelector((state) => state.loginReducer.mainMenuVisibility);

    const enableIDPAuth = useSelector(
        (state) => state.devReducer.devData.enableIDPAuth
    );
    // Global variable for dynarrace
    const userDetails = useSelector(
        (state) => state.loginReducer.hybrisUserDetails,
        shallowEqual
    );
    const { appliedCategory, appliedQuickFilter, appliedFilter } = useSelector(
        (state) => state.orderFilterReducer
    );
    const { appliedOrderType, appliedOrderStatus } = appliedFilter;

    const ToggleChecked = useSelector(
        (state) => state.ordersListReducer.ToggleChecked
    );
    const toggleCheckedDsktop = useSelector(
        (state) => state.ordersListReducer.toggleCheckedDsktop
    );
    const minCreationImpersonatedDate = useSelector(
        (state) =>
            state.impersonatedUserDataReducer.hybrisUserDetails.minCreationDate
    );
    const minCreationUserDate = useSelector(
        (state) => state.loginReducer.hybrisUserDetails.minCreationDate
    );


    const minCreationDate = isImpersonationActive
        ? minCreationImpersonatedDate
        : minCreationUserDate;

    const [firstOrderStartDate] = useState(addDaysToDate(minCreationDate, 0)); 
    const createOrderDetails = useSelector(
        (state) => state.loginReducer.createOrderDetails
    );
    const moreVisibility = useSelector(
        (state) => state.loginReducer.moreVisibility
    );
    const isLanguageChanged = useSelector(
        (state) => state.loginReducer.isLanguageChanged
    );
    const isImpersonationActive = useSelector(
        (state) => state.impersonationReducer.isImpersonationActive
    );
    const { pageRuleList, pageRuleListLoader } = useSelector(
        (state) => state.pageVisibilityRuleReducer
    );
    const appliedUserFilters = useSelector(
        (state) => state.appliedOrdersFilterReducer
    );

    const mainMenuVisibility = useSelector(state => state.loginReducer.mainMenuVisibility);

    let query = getUrlparams();
    setPageVisibilityRule(pageRuleList);
    useEffectWithErrorLogging(() => {
        setPageVisibilityRule(pageRuleList);
    }, [pageRuleList]);

    useEffectWithErrorLogging(() => {
        if (isLanguageChanged) {
            const elements = document.querySelectorAll('*');
            elements.forEach((ele) => {
                const style = window.getComputedStyle(ele);
                if (style.fontFamily.toLocaleLowerCase().includes('demi')) {
                    ele.style.fontWeight = '700';
                } else {
                    ele.style.fontWeight = '400';
                }
            });
        }
    });

    useEffectWithErrorLogging(() => {
        const isInventoryPath =
            window.location.pathname === '/inventory' ||
            window.location.pathname === '/consignmentReturnInventory';

        if (isInventoryPath) {
            window.scrollTo(0, 0);
        }
    }, [window.location.pathname]);

    useEffectWithErrorLogging(() => {
        if (
            appliedCategory.categoryName ===
            t('orders.orderQuickFilters.deniedUsageOverrides.name')
        ) {
            dispatch(setDeniedOverrideClicked(true));
        }
        if (!sessionStorage.getItem('browserState')) {
            clearStorageBrowserClose(dispatch);
            sessionStorage.setItem('browserState', true);
        }
        // dispatch(setForbiddenErrorCheckValue(forbiddenErrorCheck(instance, dispatch, logoutAPI)));
    }, []);

    useEffectWithErrorLogging(() => {
        if (
            userDetails.emailId &&
            userDetails.networkId &&
            displayDeniedOverride(
                checkImpersonationActive() || checkProxyActive()
                    ? sessionStorage.getItem('ProxyUserRole')
                    : employeeRole,
                countryISOCode
            )
        ) {
            dispatch(
                setSearchParams({
                    account: '',
                    hrefId: '',
                    physician: '',
                    surgeryDate: '',
                    networkId: '',
                    lotSerialNumber: '',
                    myCaseRef: '',
                    sapOrderId: '',
                    purchaseOrderId: '',
                    unidCaseId: '',
                    trackingNumber: '',
                    delivery: '',
                    secondaryVendorCaseNo: '',
                })
            );
            dispatch(
                setOrderDateAction({
                    firstOrderStartDate:
                        firstOrderStartDate !== ''
                            ? convertDateToISOString(
                                new Date(firstOrderStartDate),
                                'start'
                            )
                            : '',
                    orderListEndDate: endDate !== '' ? endDate : '',
                })
            );

            let orderStatus = '';

            const checkQuickFilterEmpty =
                Object.keys(appliedQuickFilter).length === 0 &&
                appliedQuickFilter.constructor === Object;

            orderStatus = !checkQuickFilterEmpty
                ? appliedCategory.categoryName ===
                    t('orders.orderListFilterDropDown.drafts')
                    ? appliedCategory.orderType
                    : appliedQuickFilter.isDefault
                        ? checkAppliedOrderStatusHandler(appliedOrderStatus)
                        : appliedQuickFilter?.statusCode &&
                            appliedQuickFilter?.statusCode !== ''
                            ? appliedQuickFilter.statusCode
                            : ''
                : checkAppliedOrderStatusHandler(appliedOrderStatus);
            if (
                (toggleCheckedDsktop || ToggleChecked) &&
                appliedCategory.categoryName ===
                t('orders.orderListFilterDropDown.all.categoryName')
            ) {
                const updateOrderStatus =
                    orderStatus !== '' ? orderStatus.split(',') : [];
                if (!updateOrderStatus.includes('10013')) {
                    updateOrderStatus.push('10013');
                }
                orderStatus =
                    updateOrderStatus.length > 0
                        ? updateOrderStatus.join(',')
                        : '';
            }
            dispatch(actionApplyFilterType('initial'));
            if (
                appliedCategory.categoryName ===
                t('orders.orderQuickFilters.deniedUsageOverrides.name')
            ) {
                dispatch(
                    getRefinedOrderListData(
                        {
                            ...appliedUserFilters,
                            filter: {
                                ...filter,
                                startDate: convertDateToISOString(
                                    new Date(firstOrderStartDate),
                                    'start'
                                ),
                                deniedOverrides: true,
                                orderType: 'ZLB',
                                orderStatusCode: '12347,12348',
                            },
                            pageLimit: PAGE_LIMIT,
                            pageNo: 0,
                        },
                        true
                    )
                );
                twoCallAPIHandler(false, false);
            } else {
                dispatch(
                    getDeniedOrder(
                        {
                            ...appliedUserFilters,
                            filter: {
                                ...filter,
                                startDate: convertDateToISOString(
                                    new Date(firstOrderStartDate),
                                    'start'
                                ),
                                deniedOverrides: true,
                                orderType: 'ZLB',
                                orderStatusCode: '12347,12348',
                            },
                            pageLimit: PAGE_LIMIT,
                            pageNo: 0,
                        },
                        true
                    )
                );
            }
        }
    }, [userDetails, isImpersonationActive]);

    // useEffectWithErrorLogging(() => {
    //     if (initiateProxyStatus === '200') {
    //         window.location.reload(true);
    //     }
    // }, [initiateProxyStatus]);

    // useEffectWithErrorLogging(() => {
    //     if (proxyTerminationStatus === '200') {
    //         window.location.reload(true);
    //     }
    // }, [proxyTerminationStatus]);

    const twoCallAPIHandler = (isPage, isDateDispatch) => {
        if (
            displayDeniedOverride(
                checkImpersonationActive() || checkProxyActive()
                    ? sessionStorage.getItem('ProxyUserRole')
                    : employeeRole,
                countryISOCode
            )
        ) {
            dispatch(
                refineFilterAndOrderList({
                    filters: {
                        ...appliedUserFilters,
                        filter: {
                            ...filter,
                            startDate: isDateDispatch
                                ? // ? orderListStartDate
                                convertDateToISOString(
                                    new Date(firstOrderStartDate),
                                    'start'
                                )
                                : filter.startDate,
                            endDate: isDateDispatch
                                ? orderListEndDate
                                : filter.endDate,
                            deniedOverrides: true,
                            orderType: 'ZLB',
                            orderStatusCode: '12347,12348',
                            assignedFlag: filter.assignedFlag,
                        },
                        pageLimit: appliedUserFilters.pageLimit,
                        pageNo: isPage ? appliedUserFilters.pageNo : 0,
                    },
                    appliedQuickFilter: appliedQuickFilter,
                    appliedCategory: appliedCategory,
                    appliedOrderType:
                        checkAppliedOrderTypeHandler(appliedOrderType),
                    appliedOrderStatus:
                        checkAppliedOrderStatusHandler(appliedOrderStatus),
                    isAwaitingPo:
                        toggleCheckedDsktop || ToggleChecked ? true : false,
                    isAwatingPOStatusCode: '10013',
                })
            );
        }
    };

    useEffectWithErrorLogging(() => {
        dispatch(
            setImpersonateCheckAction({
                isImpersonationActive: checkImpersonationActive(),
            })
        );
        const impersonatedUserId =
            query.get('impersonatedUser') || query.get('proxyUser');

        sessionStorage.setItem('impersonatedUserId', impersonatedUserId);

        if (impersonatedUserId === 'null') {
            sessionStorage.setItem('impersonationFlag', null);
        }
    }, []);
    useEffectWithErrorLogging(() => {
        document.fonts.ready.then(() => {
            setFontsLoaded(true);
        });
    }, []);

    const getsalesStructure = (userDetails) => {
        let district = 'NA'; // "District";
        if (
            userDetails?.salesStructure &&
            userDetails.salesStructure[0]?.district?.trim()?.length > 0
        ) {
            district = userDetails.salesStructure[0].district;
        }
        return district.substring(0, 25);
    };

    useEffectWithErrorLogging(() => {
        if (userDetails.emailId && userDetails.networkId) {
            const { deviceName, deviceType } = detectDeviceInfo();
            window.userInfo = `${userDetails.networkId} | ${userDetails?.displayName?.trim()
                    ? userDetails.displayName
                    : 'NA' //'Full Name'
                } | ${userDetails?.employeeRole?.trim()?.length > 0
                    ? userDetails.employeeRole.substring(0, 25)
                    : 'NA' //'Role'
                } | ${getsalesStructure(
                    userDetails
                )} | ${deviceType} | ${deviceName} | ${countryISOCode}`;
        }
        const networkId = userDetails?.networkId?.trim()
            ? userDetails.networkId
            : 'NA';
        const role = userDetails?.employeeRole?.trim()
            ? userDetails.employeeRole.substring(0, 25)
            : 'NA';
        localStorage.setItem('NetworkId', networkId);
        localStorage.setItem('Role', role);
        // i18n.changeLanguage('ja');
    }, [userDetails]);

    useEffectWithErrorLogging(() => {
        if (!enableIDPAuth) {
            dispatch(getAuthToken({ enableIDPAuth: enableIDPAuth }));
        }
    }, []);

    const loggingOut = useSelector((state) => state.loginReducer.loggingOut);
    const loggingOutSSO = useSelector(
        (state) => state.loginReducer.loggingOutSSO
    );
    const logoutStatusCode = useSelector(
        (state) => state.loginReducer.logoutStatusCode
    );
    const resettingFilters = useSelector(
        (state) => state.setfilterOptionsReducer.resettingFilters
    );

    useEffectWithErrorLogging(() => {
        /**
         * Thisis required when SSO is disabled as in that case if user is logged in and refreshes,
         * then user details are not fetched as login cmponent is not loaded.
         * But with SSO on ACCESS_TOKEN is always loaded where user details are fetched
         */
        if (localStorage.getItem('authToken')) {
            dispatch(getBannerDetails());
            dispatch(getPageVisibilityRule({ fields: 'BASIC' }));
            dispatch(getProxyList());
            dispatch(getHybrisUserDetails());
            dispatch(getConfigurations({ resourceType: 'PROCESS' }, MENU));
        }
    }, []);

    useEffectWithErrorLogging(() => {
        if (isImpersonationActive) {
            getRoutes();
        }
    }, [isImpersonationActive]);
    useNonInitialRenderHook(() => {
        if (logoutStatusCode === '10011') {
            localStorage.setItem('authToken', '');
            const toRemoveParams = query.get('impersonatedUser');
            if (toRemoveParams !== 'null') {
                const newUrl = window.location.href.split('?')[0];
                window.location.replace(newUrl);
            }

            removeImperonatedUserDetail();
            removeProxyUserDetail();
            // if the impersonation is active
            // then rremove all the parameters
            // clear the impersonation session storage
            setTimeout(() => window.location.reload(), 0);
        } else {
            setTimeout(() => window.location.reload(), 0);
        }
    }, [logoutStatusCode]);

    // useEffectWithErrorLogging(() => {
    //     if (
    //         sessionStorage.getItem('employeeRole') &&
    //         sessionStorage.getItem('employeeRole') !== 'undefined' &&
    //         localStorage.getItem('authToken')
    //     ) {
    //         if (
    //             getUser() == FIM_USER_ROLE &&
    //             (window.location.pathname == '/' ||
    //                 window.location.pathname == '/dashboard') &&
    //             pageRuleList[0]?.role == FIM_USER_ROLE &&
    //             !pageRuleListLoader
    //         ) {
    //             window.location.href = sideNavLinksWCMSLinks('dashboard');
    //         }
    //     }
    // }, [window.location.pathname, window.location, pageRuleList]);

    useEffectWithErrorLogging(() => {
        setErrorDetails('');
        let timer;
        if (
            sessionStorage.getItem('employeeRole') &&
            sessionStorage.getItem('employeeRole') !== 'undefined' &&
            !pageRuleListLoader &&
            EQUITY_USER_PAGE_VISIBILITY_RULE.length > 0
        ) {
            if (
                EQUITY_USER_PAGE_VISIBILITY_RULE.filter(
                    (item) => item.role == getUser()
                ).length === 0 &&
                (sessionStorage.getItem('impersonationFlag') === 'true' &&
                    sessionStorage.getItem('impersonatedUserId') != 'null'
                    ? true
                    : sessionStorage.getItem('employeeRole') ===
                    EQUITY_USER_PAGE_VISIBILITY_RULE[0]?.role)
            ) {
                logoutWithCleanUp(instance, dispatch, logoutAPI);
            } else if (window.location.pathname !== '/login') {
                timer = setTimeout(() => {
                    try {
                    setErrorDetails(
                        isCorrectURL(
                            routes,
                            window.location.pathname,
                            EQUITY_USER_PAGE_VISIBILITY_RULE.filter(
                                (item) => item.role == getUser()
                            )[0]?.urlAccess,
                            {
                                ...createOrderDetails,
                                ...moreVisibility,
                                ...mainMenuVisibility
                                // ...inventoryVisibility,
                            }
                        )
                    );
                    } catch (error) {
                logErrorToConsole(error);
            }
                }, 5000);
            }
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [history, pageRuleListLoader, createOrderDetails, moreVisibility, mainMenuVisibility]);

    const isSsoEnable = process.env.ENABLE_SSO;

    const checkAppliedOrderTypeHandler = (appliedOrderType) => {
        let orderType = '';

        const selectedOrderType = appliedOrderType
            ?.filter((x) => x.isChecked === true)
            ?.map((a) => {
                if (a.statusCode === 'ALL_ORDERTYPE') {
                    return '';
                }
                return a.statusCode;
            });

        orderType =
            selectedOrderType?.length > 0 ? selectedOrderType.join(',') : '';

        return orderType;
    };
    const checkAppliedOrderStatusHandler = (appliedOrderStatus) => {
        let orderStatus = '';

        const selectedOrderStatus = appliedOrderStatus
            ?.filter((x) => x.isChecked === true)
            ?.map((a) => {
                if (a.statusCode === 'ALL_ORDERSTATUS') {
                    return '';
                }
                return a.statusCode;
            });

        orderStatus =
            selectedOrderStatus?.length > 0
                ? selectedOrderStatus.join(',')
                : '';
        return orderStatus;
    };

    const getRoutes = () => {
        return isSsoEnable
            ? routes.map((route, index) => (
                <Route
                    key={index}
                    exact={route.exact}
                    path={route.path}
                    component={
                        route.path === '/adminView' && adminMainMenuVisibility
                            ? Admin
                            : route.path === '/'
                                ? ((userDetails.employeeRole === CC_USER_ROLE ||
                                    userDetails.employeeRole ===
                                    ITA_USER_ROLE) &&
                                    route.path === '/' &&
                                    !checkImpersonationActive() &&
                                    !checkProxyActive()) ||
                                    ((checkImpersonationActive() ||
                                        checkProxyActive()) &&
                                        (sessionStorage.getItem('ProxyUserRole') ===
                                            CC_USER_ROLE ||
                                            sessionStorage.getItem(
                                                'ProxyUserRole'
                                            ) === ITA_USER_ROLE) &&
                                        route.path === '/')
                                    ? adminMainMenuVisibility ? Admin : Orders
                                    : (userDetails.employeeRole ===
                                        CSR_USER_ROLE &&
                                        route.path === '/' &&
                                        !checkImpersonationActive() &&
                                        !checkProxyActive()) ||
                                        ((checkImpersonationActive() ||
                                            checkProxyActive()) &&
                                            sessionStorage.getItem(
                                                'ProxyUserRole'
                                            ) === CSR_USER_ROLE &&
                                            route.path === '/') && ordersMainMenuVisibility
                                        ? Orders
                                        : ((userDetails.employeeRole ===
                                            FIM_USER_ROLE ||
                                            userDetails.employeeRole ===
                                            FIA_USER_ROLE) &&
                                            route.path === '/' &&
                                            !checkImpersonationActive() &&
                                            !checkProxyActive()) ||
                                            ((checkImpersonationActive() ||
                                                checkProxyActive()) &&
                                                (sessionStorage.getItem(
                                                    'ProxyUserRole'
                                                ) === FIM_USER_ROLE ||
                                                    sessionStorage.getItem(
                                                        'ProxyUserRole'
                                                    ) === FIA_USER_ROLE) &&
                                                route.path === '/')
                                            ? inventoryMainMenuVisibility ? ReceiveSets : Orders
                                            : calendarMainMenuVisibility ? Home : Orders
                                : route.component
                    }
                ></Route>
            ))
            : routes.map((route, index) => {
                return (
                    <AuthenticatedRoute
                        key={index}
                        exact={route.exact}
                        path={route.path}
                        component={
                            route.path === '/adminView' && adminMainMenuVisibility
                                ? Admin
                                : route.path === '/'
                                    ? ((userDetails.employeeRole ===
                                        CC_USER_ROLE ||
                                        userDetails.employeeRole ===
                                        ITA_USER_ROLE) &&
                                        route.path === '/' &&
                                        !checkImpersonationActive() &&
                                        !checkProxyActive()) ||
                                        ((checkImpersonationActive() ||
                                            checkProxyActive()) &&
                                            (sessionStorage.getItem(
                                                'ProxyUserRole'
                                            ) === CC_USER_ROLE ||
                                                sessionStorage.getItem(
                                                    'ProxyUserRole'
                                                ) === ITA_USER_ROLE) &&
                                            route.path === '/')
                                        ? adminMainMenuVisibility ? Admin : Orders
                                        : (userDetails.employeeRole ===
                                            CSR_USER_ROLE &&
                                            route.path === '/' &&
                                            !checkImpersonationActive() &&
                                            !checkProxyActive()) ||
                                            ((checkImpersonationActive() ||
                                                checkProxyActive()) &&
                                                sessionStorage.getItem(
                                                    'ProxyUserRole'
                                                ) === CSR_USER_ROLE &&
                                                route.path === '/') && ordersMainMenuVisibility
                                            ? Orders
                                            : ((userDetails.employeeRole ===
                                                FIM_USER_ROLE ||
                                                userDetails.employeeRole ===
                                                FIA_USER_ROLE) &&
                                                route.path === '/' &&
                                                !checkImpersonationActive() &&
                                                !checkProxyActive()) ||
                                                ((checkImpersonationActive() ||
                                                    checkProxyActive()) &&
                                                    (sessionStorage.getItem(
                                                        'ProxyUserRole'
                                                    ) === FIM_USER_ROLE ||
                                                        sessionStorage.getItem(
                                                            'ProxyUserRole'
                                                        ) === FIA_USER_ROLE) &&
                                                    route.path === '/')
                                                ? inventoryMainMenuVisibility ? ReceiveSets : Orders
                                                : calendarMainMenuVisibility ? Home : Orders
                                    : route.component
                        }
                    ></AuthenticatedRoute>
                );
            });
    };
    useNonInitialRenderHook(() => {
        if (!userDetails.uid && !isSsoEnable) {
            window.location.href = '/login';
        }
    }, [userDetails.uid, isSsoEnable]);
    const handleErrorDetails = () => {
        return window.location.pathname != '/login' &&
            window.location.pathname != '/' &&
            errorDetails
            ? true
            : false;
    };
    return (
        <AppErrorBoundary>
        <>
            {isSsoEnable ? (
                <div>
                    <UnauthenticatedTemplate>
                        <div>
                            <Spinner id="app-loading-spinner-unauthenticatedUser"
                                animation="border"
                                variant="primary"
                            ></Spinner>
                            <SSO />
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>
                        <Router>
                            <AccessToken />
                        </Router>
                        {Object.keys(userDetails).length > 0 &&
                            (validateUserRole(userDetails.employeeRole) ||
                                (sessionStorage.getItem('impersonatedUserId') !==
                                    'null' &&
                                    query.get('impersonatedUser') !== 'null')) ? (
                            <Router>
                                <SessionTimeout />
                                <PageLayout>
                                    <PageMaxWidthWrapper>
                                        {loggingOutSSO ||
                                            resettingFilters ||
                                            pageRuleListLoader ? (
                                            <Spinner id="app-loading-spinner-validUser"
                                                animation="border"
                                                variant="primary"
                                            ></Spinner>
                                        ) : handleErrorDetails() ? (
                                            <Switch>
                                                <Route
                                                    exact
                                                    path="/error"
                                                    component={ErrorComponent}
                                                ></Route>
                                                <Redirect
                                                    to={{
                                                        ...getRoutePathWithParams(
                                                            '/error'
                                                        ),
                                                        state: {
                                                            errorDetails:
                                                                errorDetails,
                                                        },
                                                    }}
                                                />
                                            </Switch>
                                        ) : (
                                            <Switch>
                                                {getRoutes()}
                                                <Route
                                                    path="*"
                                                    component={() => (
                                                        <Redirect
                                                            to={getRoutePathWithParams(
                                                                '/'
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Switch>
                                        )}
                                    </PageMaxWidthWrapper>
                                </PageLayout>
                            </Router>
                        ) : (
                            <Spinner id="app-loading-spinner-inValidState"
                                animation="border"
                                variant="primary"
                            ></Spinner>
                        )}
                    </AuthenticatedTemplate>
                </div>
            ) : (
                <Router>
                    <SessionTimeout />

                    <PageLayout>
                        <PageMaxWidthWrapper>
                            {loggingOut ||
                                resettingFilters ||
                                logoutStatusCode === '10011' ||
                                pageRuleListLoader ? (
                                <Spinner id="app-loading-spinner-ssoDisabled"
                                    animation="border"
                                    variant="primary"
                                ></Spinner>
                            ) : handleErrorDetails() ? (
                                <Switch>
                                    <Route
                                        exact
                                        path="/error"
                                        component={ErrorComponent}
                                    ></Route>
                                    <Redirect
                                        to={{
                                            ...getRoutePathWithParams('/error'),
                                            state: {
                                                errorDetails: errorDetails,
                                            },
                                        }}
                                    />
                                </Switch>
                            ) : (
                                <Switch>
                                    <Route
                                        exact
                                        path="/login"
                                        component={Login}
                                    ></Route>
                                    {getRoutes()}
                                </Switch>
                            )}
                        </PageMaxWidthWrapper>
                    </PageLayout>
                </Router>
            )}
        </>
        </AppErrorBoundary>
    );
};

export default App;

const PageMaxWidthWrapper = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 1136px;
    margin: auto;
    padding: 0 20px 0 20px;
    max-height: 100%;
    overflow-y: visible;
    @media only screen and (max-width: 1200px) {
        max-width: 1020px;
    }

    @media only screen and (max-width: 1140px) {
        max-width: 980px;
    }
    @media only screen and (max-width: 1100px) {
        max-width: 955px;
    }
    @media only screen and (max-width: 1085px) {
        max-width: 930px;
    }
    ${media.mediumDevice`
        max-width: 924px;
    `}
    ${media.mobileDevice`
        display: block;
        padding: 0;
        max-height: unset;
        overflow-y: unset;
    `}
`;
